// Sidekick agent sidebar — generic chat + suggested-edits surface
// rendered alongside the CLW review pane. Platform-agnostic: takes an
// agent `session` object (shape mirrors useGtCtsReviewerSession) and a
// `config` of platform-specific copy. When `session` is null the
// sidebar still mounts as a shell and shows `config.fallbackMessage`.
//
// State the component owns:
//   - aiDraft (input text)
//   - scroll sentinel + auto-scroll effect
// Everything else (the session itself, suggestion status, apply/dismiss
// callbacks, lifecycle effects) is owned by the parent so each platform
// can wire its own agent protocol.

import { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { AI_MD_PLUGINS, AI_MD_COMPONENTS } from './markdownConfig';
import { mergeFeed } from './feedOrder';
import { isAgentWorking } from './agentBusy';

const fmtInt = (n) => Number(n || 0).toLocaleString();

export function AgentSidebar({
  // Open/close control (parent owns the state; close button calls back).
  open,
  setOpen,
  // Drag handle on the LEFT edge — width state lives in the parent so
  // the CSS var stays in sync with the pane shell.
  onResizeStart,
  // Agent session — null when no agent is wired for the current
  // destination. Shape: { messages, suggestions, status, sessionId,
  // interrupting, usage, artifactErrors, connectionError,
  // pendingToolCalls, sendMessage, interrupt, reconnect, createSession,
  // connect }.
  session,
  // Per-platform copy + tunables. See defaults below.
  config = {},
  // Context line shown when no session is active yet (or before the
  // sessionId is known).
  context = {},
  // Suggestion UI state — owned by parent so it survives mount cycles.
  suggestionStatus = {},
  suggestionErrors = {},
  onApplySuggestion,
  onApplyAllSuggestions,
  onDismissSuggestion,
  onClearSuggestionError,
  // ── Mode switching (optional) ──────────────────────────────────────
  // When `modes` is omitted the sidebar renders exactly as it always has,
  // so the existing reviewer callers are untouched. Pass a modes array to
  // get a switcher in the header:
  //   [{ key, label, title?, available?, unavailableReason? }]
  // The parent owns which mode is active and swaps the `session` prop to
  // match — this component only renders the control.
  modes = null,
  mode = null,
  setMode,
  // Host content INTERLEAVED into the conversation by timestamp:
  //   [{ key, ts, node }]
  // For anything that happened at a point in time — the bulk editor's
  // confirmation card, its result note, its failure banner. This replaced a
  // `feedSlot` rendered after the whole message list, which made position
  // structural rather than chronological: every notice sat pinned at the bottom,
  // so a note about an action three turns ago looked like it belonged to the
  // newest message.
  feedItems = null,
  // Standing scope line under the context bar — e.g. "Scoped to 3 selected
  // rows", with whatever control clears it. Rendered above the messages so the
  // user can see the agent is not addressing the whole plan BEFORE they ask for
  // a change, not after they read a preview with a surprising row count.
  scopeNote = null,
  // ── Embedded mode ─────────────────────────────────────────────────
  // Render the agent as the CONTENTS of a container the host already owns,
  // rather than as its own slide-over. The Campaign Grid's detail panel needs
  // this: it has a head and a close button of its own, and stacking a second
  // navy head inside it would read as two panels wedged together.
  //
  // What changes: no positioning wrapper, no drag handle, no head. The "New
  // session" control moves into the context strip, since that is the only chrome
  // left to carry it. Everything below the head — modes, banners, the feed, the
  // composer — is identical, which is the point: one implementation of the
  // conversation, two frames around it.
  embedded = false,
  // Show the session/context strip under the head. Hosts that already state what
  // is being viewed in their own chrome turn it off — on the Campaign Grid panel
  // it repeated the campaign the panel header names, and before a session exists
  // it read "Viewing: overview — 0 suggestions", which describes nothing.
  //
  // Turning it off also drops "New session" with it, since that strip is the only
  // chrome carrying it in embedded mode. That is deliberate rather than a loss:
  // on those surfaces the session is keyed to a run, and running again opens a
  // fresh one.
  showContext = true,
}) {
  const {
    title = 'Sidekick agent',
    note = 'can review & suggest edits',
    placeholder,            // { title, body, sectionLabel, prompts: [string | { label, draft }] }
    autoQaMessage,          // shown while priming; pass null/empty to skip
    autoQaRole = 'Sidekick agent · auto-QA',
    // Shown with typing dots while the session is still coming up — before
    // there is a sessionId, and (for agents with no autoQaMessage) through
    // priming until the first agent message lands. Without it the bulk editor's
    // first few seconds were an empty panel with a disabled input, which reads
    // as broken rather than as loading.
    loadingMessage = 'Starting up…',
    inputPlaceholder = 'Type a message…',
    inputPlaceholderNoSession = 'Agent coming soon…',
    fallbackMessage,        // ReactNode rendered when session is null
    suggestionHint,         // explanation text under the suggestion chips
    // Non-chat surfaces. `fallbackRaw` renders the fallbackMessage directly in
    // the feed instead of wrapping it in an agent message bubble (used by the
    // Runs tab, which brings its own list chrome), and `hideInput` drops the
    // composer entirely — there is no conversation to type into.
    fallbackRaw = false,
    hideInput = false,
  } = config;
  const { activeGroup, rowsCount, rowLabel } = context;

  const hasSession = Boolean(session);
  const [aiDraft, setAiDraft] = useState('');
  // Auto-scroll the feed to the bottom on new content. Pointed at a
  // sentinel <div /> at the end of the messages container so we don't
  // have to manually compute scrollHeight. Triggers on every visible
  // change: messages length, suggestions length, status (covers typing
  // dots appearing/disappearing), and sidebar open.
  const aiBottomRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    requestAnimationFrame(() => {
      aiBottomRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
    });
  }, [
    open,
    session?.messages?.length,
    session?.suggestions?.length,
    session?.status,
    // A notice can arrive without a new message (a staged batch, a failure), and
    // it is the thing the user needs to see.
    feedItems?.length,
  ]);

  const send = async () => {
    const text = aiDraft.trim();
    if (!text || !hasSession) return;
    // Session may not exist yet if the user typed before autoStart
    // fired (or autoStart was guarded out). Bootstrap on demand here
    // so the message reliably lands.
    let id = session.sessionId;
    if (!id) id = await session.createSession?.();
    // Creation failed (validation error / backend down) — keep the
    // draft so the user can retry; the hook has already surfaced the
    // error via session.status/error.
    if (!id) return;
    if (!session.sessionId) session.connect?.(id);
    // Pass the id explicitly: on the bootstrap path session.sendMessage
    // still closes over the pre-creation sessionId (null) — without the
    // override the very first message is silently dropped.
    session.sendMessage(text, id);
    setAiDraft('');
  };

  // "New session" clears the CONVERSATION. It is not the same thing as an
  // agent's own re-do control (the optimizer's "New optimizer run" re-analyses
  // the campaign), so it carries a ↻ and its own class rather than borrowing the
  // head's close-button styling — it had been indistinguishable from chrome, and
  // users asked where it was while looking straight at it.
  const newSessionButton = hasSession && session.newSession ? (
    <button
      type="button"
      className="clw-ai-newsession"
      onClick={() => {
        if (session.status === 'creating') return;
        if (window.confirm('Start a new session? The current conversation will be cleared.')) {
          session.newSession();
        }
      }}
      disabled={session.status === 'creating'}
      title="Start a fresh agent session — clears this conversation. Does not re-run anything."
    >
      <span aria-hidden="true">↻</span> New session
    </button>
  ) : null;

  return (
    <div className={embedded ? 'clw-ai-embedded' : `clw-ai-sidebar ${open ? 'open' : ''}`}>
      {!embedded && open && (
        <span
          className="clw-ai-resize"
          onMouseDown={onResizeStart}
          title="Drag to resize"
        />
      )}
      {!embedded && (
        <div className="clw-ai-head">
          <div className="clw-ai-head-icon">✦</div>
          <div className="clw-ai-head-title">{title}</div>
          <div className="clw-ai-head-note">{note}</div>
          {newSessionButton}
          <button className="clw-ai-head-close" onClick={() => setOpen(false)}>✕</button>
        </div>
      )}
      {/* Mode switcher — Bulk edit vs Review QA. A mode with
          available === false still renders (so the user learns it exists on
          the platforms that have it) but is disabled and explains itself. */}
      {Array.isArray(modes) && modes.length > 1 && (
        <div className="clw-ai-modes" role="tablist">
          {modes.map((m) => {
            const isActive = m.key === mode;
            const unavailable = m.available === false;
            return (
              <button
                key={m.key}
                role="tab"
                aria-selected={isActive}
                className={`clw-ai-mode ${isActive ? 'active' : ''} ${unavailable ? 'unavailable' : ''}`}
                disabled={unavailable}
                title={unavailable ? (m.unavailableReason || 'Not available for this plan') : (m.title || m.label)}
                onClick={() => { if (!unavailable && !isActive) setMode?.(m.key); }}
              >{m.label}</button>
            );
          })}
        </div>
      )}
      {showContext && (
      <div className={`clw-ai-context${embedded ? ' embedded' : ''}`}>
        <span className="clw-ai-context-text">
          {hasSession && session.sessionId ? (
            <>
              Session <strong>{session.sessionId.slice(0, 12)}…</strong> · {session.status}
              {(session.usage?.input_tokens > 0 || session.usage?.output_tokens > 0) && (
                <span style={{ marginLeft: 8, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--g4)' }} title="Tokens used this session">
                  · {fmtInt((session.usage?.input_tokens || 0) + (session.usage?.output_tokens || 0))} tok
                </span>
              )}
            </>
          ) : (
            <>Viewing: <strong>{(activeGroup || 'overview')} — {fmtInt(rowsCount)} {rowLabel}</strong></>
          )}
        </span>
        {/* Embedded has no head to hang this off, and losing "New session"
            entirely would strand a user in a conversation they want to restart. */}
        {embedded && newSessionButton}
      </div>
      )}
      {scopeNote}
      {/* Banners — surface artifact errors + connection drops at the
          top of the messages stack so they're seen before the user
          scrolls. */}
      {hasSession && session.artifactErrors?.length > 0 && (
        <div className="clw-ai-banner clw-ai-banner-warn">
          <strong>Partial artifacts loaded.</strong> {session.artifactErrors.join(' · ')}. The agent may operate in a degraded mode — re-run validation and reopen the sidebar.
        </div>
      )}
      {hasSession && session.connectionError && (
        <div className="clw-ai-banner clw-ai-banner-warn">
          <span>{session.connectionError}</span>
          <button type="button" onClick={() => session.reconnect?.()}>Reconnect</button>
        </div>
      )}
      {/* Hard errors — a failed session create (bad/absent payload) or a
          rejected send. Without this the input just goes disabled with the
          user's text still in it and no explanation anywhere on screen. */}
      {hasSession && session.error && (
        <div className="clw-ai-banner clw-ai-banner-error">
          <span>{session.error}</span>
          {session.newSession && (
            <button
              type="button"
              onClick={() => session.newSession()}
              disabled={session.status === 'creating'}
            >Retry</button>
          )}
        </div>
      )}
      <div className="clw-ai-messages">
        {hasSession ? (
          <>
            {/* Persistent placeholder — pinned at the top of the feed.
                Stays visible across the whole session so the prompt
                chips remain a one-click shortcut even after auto-QA
                has produced output. Doesn't compete with the auto-QA
                bubble — they stack, with the placeholder always above. */}
            {placeholder && (
              <div className="clw-ai-placeholder">
                {placeholder.title && (
                  <div className="clw-ai-placeholder-title">{placeholder.title}</div>
                )}
                {placeholder.body && (
                  <div className="clw-ai-placeholder-sub">{placeholder.body}</div>
                )}
                {placeholder.sectionLabel && (placeholder.prompts?.length || 0) > 0 && (
                  <div className="clw-ai-placeholder-section">{placeholder.sectionLabel}</div>
                )}
                {(placeholder.prompts || []).map((p, i) => {
                  // Prompts can be plain strings (the same text fills
                  // the chip label AND the draft on click) or an object
                  // { label, draft } when the rendered label differs
                  // from the submitted text (e.g. inline <em> markup).
                  const label = typeof p === 'string' ? p : p.label;
                  const draft = typeof p === 'string' ? p : (p.draft || p.label);
                  return (
                    <button
                      key={i}
                      className="clw-ai-prompt-chip"
                      type="button"
                      onClick={() => setAiDraft(draft)}
                    >{label}</button>
                  );
                })}
              </div>
            )}
            {/* First-load bubble — the session is being created, or it exists
                and is chewing through the priming message. Two cases, one
                bubble:
                  · no sessionId yet → the POST is in flight (or auto-start is
                    armed and about to fire). session.starting is what makes
                    this safe: on a surface whose payload will never be ready it
                    stays false, so this can't sit spinning forever.
                  · sessionId, zero messages, and no autoQaMessage configured →
                    the bulk editor's priming turn, which used to render as an
                    empty panel with a dead input.
                Agents that DO configure autoQaMessage keep their own bubble for
                the second case; the two are mutually exclusive by construction. */}
            {session.messages.length === 0
              && !session.error
              && session.status !== 'error' && session.status !== 'terminated' && session.status !== 'finalized'
              && ((session.starting && !session.sessionId) || (session.sessionId && !autoQaMessage)) && (
              <div className="clw-ai-msg agent">
                <div className="clw-ai-msg-role">{title}</div>
                <div className="clw-ai-msg-bubble">
                  <div style={{ marginBottom: 8 }}>{loadingMessage}</div>
                  <div className="clw-ai-typing">
                    <span className="clw-ai-dot" /><span className="clw-ai-dot" /><span className="clw-ai-dot" />
                  </div>
                </div>
              </div>
            )}
            {/* Auto-QA bubble — shown WHILE the backend's priming
                user.message is being processed by the agent. Anchored
                on sessionId so the brief streaming → running status
                hop doesn't flicker it off. */}
            {autoQaMessage && session.messages.length === 0 && session.sessionId && session.status !== 'error' && session.status !== 'terminated' && session.status !== 'finalized' && (
              <div className="clw-ai-msg agent">
                <div className="clw-ai-msg-role">{autoQaRole}</div>
                <div className="clw-ai-msg-bubble">
                  <div style={{ marginBottom: 8 }}>{autoQaMessage}</div>
                  <div className="clw-ai-typing">
                    <span className="clw-ai-dot" /><span className="clw-ai-dot" /><span className="clw-ai-dot" />
                  </div>
                </div>
              </div>
            )}
            {/* Conversation and host notices, merged by timestamp — see
                feedOrder.mergeFeed. Interleaving is what stops a note about an
                action three turns ago from sitting pinned at the bottom, looking
                like it belongs to the newest message. */}
            {mergeFeed(session.messages, feedItems).map((entry) => (
              entry.kind === 'item' ? (
                <div key={entry.key}>{entry.node}</div>
              ) : (
                <div key={entry.key} className={`clw-ai-msg ${entry.message.role}`}>
                  <div className="clw-ai-msg-role">{entry.message.role === 'agent' ? title : 'You'}</div>
                  <div className="clw-ai-msg-bubble">
                    {entry.message.role === 'agent' ? (
                      <div className="clw-ai-md">
                        <ReactMarkdown remarkPlugins={AI_MD_PLUGINS} components={AI_MD_COMPONENTS}>
                          {entry.message.text || ''}
                        </ReactMarkdown>
                      </div>
                    ) : (
                      entry.message.text
                    )}
                  </div>
                </div>
              )
            ))}
            {session.suggestions.length > 0 && (
              <div className="clw-ai-msg agent">
                <div className="clw-ai-msg-role" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>Suggested edits</span>
                  {session.suggestions.some((s) => !suggestionStatus[s.key]) && (
                    <button
                      type="button"
                      className="clw-ai-suggestion-applyall"
                      onClick={() => onApplyAllSuggestions?.(session.suggestions.filter((s) => !suggestionStatus[s.key]))}
                      title="Apply every pending suggestion via the agent"
                    >Apply all</button>
                  )}
                </div>
                <div className="clw-ai-suggestions">
                  {session.suggestions.map((s) => {
                    const state = suggestionStatus[s.key];
                    const err = suggestionErrors[s.key];
                    const disabled = state === 'applying' || state === 'staged' || state === 'applied' || state === 'dismissed';
                    return (
                      <div key={s.key} className={`clw-ai-suggestion ${state || ''} ${err ? 'failed' : ''}`} style={{ flexBasis: '100%' }}>
                        <div style={{ display: 'inline-flex', alignItems: 'stretch' }}>
                          <button
                            type="button"
                            className="clw-ai-suggestion-pill"
                            disabled={disabled}
                            onClick={() => { onClearSuggestionError?.(s.key); onApplySuggestion?.(s); }}
                            title={
                              state === 'applied' ? 'Applied + validated'
                              : state === 'staged' ? 'Staged — will be persisted when the batch commits'
                              : state === 'applying' ? 'Applying…'
                              : state === 'dismissed' ? 'Dismissed'
                              : err ? `Last attempt failed — click to retry. ${err}`
                              : `Click to apply via agent — ${s.reason}`
                            }
                          >
                            <span className="clw-ai-suggestion-loc">{s.path}</span>
                            <em className="clw-ai-suggestion-val">→ {typeof s.suggested_value === 'string' ? s.suggested_value : JSON.stringify(s.suggested_value)}</em>
                            {state === 'applying' && <span className="clw-ai-suggestion-tag applying">applying…</span>}
                            {state === 'staged' && <span className="clw-ai-suggestion-tag staged">staged</span>}
                            {state === 'applied' && <span className="clw-ai-suggestion-tag ok">✓ applied</span>}
                            {state === 'dismissed' && <span className="clw-ai-suggestion-tag dim">dismissed</span>}
                            {err && !state && <span className="clw-ai-suggestion-tag failed">retry</span>}
                          </button>
                          {!state && (
                            <button
                              type="button"
                              className="clw-ai-suggestion-dismiss"
                              onClick={() => onDismissSuggestion?.(s)}
                              title="Dismiss this suggestion"
                            >✕</button>
                          )}
                        </div>
                        {err && (
                          <div className="clw-ai-suggestion-error">{err}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {suggestionHint && (
                  <div className="clw-ai-suggestion-hint">{suggestionHint}</div>
                )}
              </div>
            )}
            {/* Trailing typing dots — show ONLY while the agent is genuinely
                producing or in a tool roundtrip. The `streaming`-is-ambiguous
                reasoning that makes this non-obvious now lives in
                ./agentBusy.js, which both this and the optimizer's action bar
                read, because deriving it a second time got it wrong. */}
            {session.messages.length > 0 && isAgentWorking(session) && (
              <div className="clw-ai-msg agent">
                <div className="clw-ai-msg-role">{title}</div>
                <div className="clw-ai-msg-bubble">
                  <div className="clw-ai-typing">
                    <span className="clw-ai-dot" /><span className="clw-ai-dot" /><span className="clw-ai-dot" />
                  </div>
                </div>
              </div>
            )}
            {/* Agent-side errors (Anthropic SDK / SSE disconnects /
                transient backend failures) are logged but not shown
                in the chat surface — they're noise for the user. The
                transient connectionError banner above still surfaces
                "Reconnect" affordances when the stream drops. */}
          </>
        ) : fallbackRaw ? (
          fallbackMessage
        ) : (
          <div className="clw-ai-msg agent">
            <div className="clw-ai-msg-role">{title}</div>
            <div className="clw-ai-msg-bubble">{fallbackMessage}</div>
          </div>
        )}
        {/* Sentinel — useEffect scrolls this into view on new content
            so the AI feed always pins to the latest message. */}
        <div ref={aiBottomRef} />
      </div>
      {!hideInput && (
        <div className="clw-ai-input-wrap">
          <textarea
            className="clw-ai-input"
            rows={2}
            placeholder={hasSession
              ? (session.status === 'creating' ? 'Starting session…' : inputPlaceholder)
              : inputPlaceholderNoSession}
            value={aiDraft}
            disabled={!hasSession || session.status === 'creating' || session.status === 'error' || session.status === 'terminated'}
            onChange={(e) => setAiDraft(e.target.value)}
            onInput={(e) => {
              // Auto-grow: reset → measure → set. Capped via CSS max-height.
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(192, e.target.scrollHeight) + 'px';
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
          />
          <button
            className="clw-ai-send"
            disabled={!hasSession || !aiDraft.trim() || session.status === 'creating'}
            onClick={send}
          >↑</button>
        </div>
      )}
    </div>
  );
}
